import {
  Box,
  Button,
  ButtonGroup,
  Card,
  FormField,
  Heading,
  Icon,
  Label,
  Modal,
  Paragraph,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconMailFilled } from '@zillow/constellation-icons';

import { useHistory } from 'react-router';
import { useLeasingUrlParams } from '../hooks';
import { useState } from 'react';

import FaqAccordion from './Widgets/FaqAccordion';
import { useLeaseExtensionQuery, useSendForOnlineSigningMutation } from '../utils/queries';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import LeaseExtension from '../models/LeaseExtension';
import { FULLSTORY_EXCLUDE } from '../constants/analytics';
import WhoHasSigned from './Widgets/WhoHasSigned';
import type { LeaseActionProps } from '../models/Types';

export default function RequestLeaseExtensionSignatureModal({ terminateActiveAction, lease }: LeaseActionProps) {
  const [personalNote, setPersonalNote] = useState(
    'Here’s the lease renewal agreement for you to look over. If everything looks good, sign before the start date. I’ll sign after everyone else. Thanks!',
  );
  const enum ModalContentState {
    preSend = 'preSend',
    postSent = 'postSent',
  }
  const [modalContentState, setModalContentState] = useState(ModalContentState.preSend);

  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;
  const history = useHistory();
  const latestExtension = lease?.latestExtension ?? null;

  const { renewalId } = useLeasingUrlParams();
  const extensionId = renewalId || latestExtension?.id || '';
  const leaseExtensionQuery = useLeaseExtensionQuery(extensionId);
  const leaseExtension = new LeaseExtension(leaseExtensionQuery?.data);
  const sendForOnlineSigningMutation = useSendForOnlineSigningMutation();

  function onPersonalNoteChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setPersonalNote(value);
  }

  async function onSendRequestforSignatureClick() {
    await sendForOnlineSigningMutation.mutateAsync({
      postData: {
        leaseArtifactId: extensionId as unknown as number,
        leaseArtifactType: LeaseArtifactType.LEASE_EXTENSION_RENEWAL,
        message: personalNote || '',
      },
    });
    setModalContentState(ModalContentState.postSent);
  }

  function onGoBackToYourDashboardClick() {
    terminateActiveAction();
    history.push(LeaseExtension.getDashboardPath());
  }

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'sm' }}
      open
      onOpenChange={(open) => {
        if (!open) {
          terminateActiveAction();
        }
      }}
      header={
        modalContentState === ModalContentState.preSend ? (
          <Heading level={2}>Request signatures</Heading>
        ) : (
          <Heading level={2}>Signatures sent</Heading>
        )
      }
      body={
        <>
          {modalContentState === ModalContentState.preSend ? (
            <>
              <Paragraph css={{ marginBottom: 'tight' }}>
                We’ll email the below people a link to view and sign this lease renewal agreement. They’ll need to log
                in to do so. You’ll be able to track who has signed from your leases dashboard.
              </Paragraph>
              {leaseExtension?.tenants?.length > 0 && (
                <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
                  {leaseExtension.tenants.map((tenant, index) => (
                    <Box
                      key={tenant.email}
                      css={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, 1fr)',
                        gap: 'tighter',
                        marginBottom: index === leaseExtension.tenants.length - 1 ? undefined : 'tight',
                      }}
                    >
                      <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                        <Heading
                          level={2}
                          css={{
                            textStyle:
                              'responsive.body-lg-bold' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                          }}
                        >
                          Tenant #{index + 1}
                        </Heading>
                      </Box>
                      <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }} className={FULLSTORY_EXCLUDE}>
                        <Text>{`${tenant.firstName} ${tenant.lastName}`}</Text>
                        <Paragraph>{tenant.email}</Paragraph>
                      </Box>
                    </Box>
                  ))}
                </Card>
              )}
              <FormField
                css={{ marginBottom: 'tight' }}
                label={<Label required>Add a personal note</Label>}
                control={
                  <Textarea
                    css={{ resize: 'vertical' }}
                    rows={6}
                    value={personalNote || ''}
                    onChange={onPersonalNoteChange}
                  />
                }
              />
              <FaqAccordion
                initialExpandedIndex={0}
                title="You may be wondering…"
                sections={[
                  {
                    label: 'What happens next?',
                    description: (
                      <Text>
                        Your recipients will get an email with a link to view and sign this lease renewal agreement.
                        <br />
                        <br />
                        If changes are needed, first cancel signing. This cancels any signatures and allows you to edit
                        the renewal agreement. Once your changes are made, request signatures again.
                      </Text>
                    ),
                  },
                  {
                    label: 'How do I download a copy of this lease renewal agreement?',
                    description: (
                      <Text>
                        You can download a complete draft of your lease renewal agreement from your leases dashboard or
                        from a confirmation screen after you request signatures. The file will be suitable to review and
                        print, but it is not meant to be signed.
                      </Text>
                    ),
                  },
                  {
                    label: 'How else can I make sure this lease renewal agreement is valid?',
                    description: (
                      <Text>
                        First off, do not hand-write changes on a printed copy of the renewal agreement. If you need to
                        make changes, do that here in Zillow Rental Manager then re-share the renewal agreement with
                        your tenants.
                      </Text>
                    ),
                  },
                ]}
              />
            </>
          ) : (
            <>
              <Icon size="lg" role="img" css={{ marginBottom: 'tight', color: 'icon.feedback.success.strong' }}>
                <IconMailFilled />
              </Icon>
              <Heading
                level={3}
                css={{
                  textStyle:
                    'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                  marginBottom: 'tight',
                }}
              >
                Request for signatures sent
              </Heading>
              <Paragraph css={{ marginBottom: 'tight' }}>
                Your tenants will get an email linking them to the lease renewal agreement for e-signing. Traditionally
                tenants sign first, then landlords.
              </Paragraph>
              <WhoHasSigned leaseExtensionId={extensionId} isInitiallyExpanded />
              <Box css={{ marginTop: 'tight' }} />
              <FaqAccordion
                initialExpandedIndex={0}
                title="You may be wondering…"
                sections={[
                  {
                    label: 'What happens next?',
                    description: (
                      <Text>
                        Now you can track who’s signed from your leases dashboard.
                        <br />
                        <br />
                        Note that you will need to request signatures again if everyone does not sign the lease renewal
                        agreement before the date it’s scheduled to go into effect.
                      </Text>
                    ),
                  },
                  {
                    label: 'What if I need to change the lease renewal agreement before everyone signs?',
                    description: (
                      <Text>
                        If changes are needed, first cancel signing. This cancels any signatures and allows you to edit
                        the lease renewal agreement. Once your changes are made, request signatures again.
                      </Text>
                    ),
                  },
                ]}
              />
            </>
          )}
        </>
      }
      footer={
        modalContentState === ModalContentState.preSend ? (
          <ButtonGroup aria-label="request signature options">
            <TextButton tone="brand" onClick={terminateActiveAction} disabled={isFetching || isMutating}>
              Back
            </TextButton>
            <Button
              tone="brand"
              emphasis="filled"
              disabled={!personalNote.trim() || isFetching || isMutating}
              onClick={onSendRequestforSignatureClick}
              loading={isFetching || isMutating}
            >
              Send request for signatures
            </Button>
          </ButtonGroup>
        ) : (
          <TextButton tone="brand" onClick={onGoBackToYourDashboardClick}>
            Go to your dashboard
          </TextButton>
        )
      }
    />
  );
}
